import { useRef, useState } from "react";

import { LP_COLUMN_MIN_WIDTH } from "../constants";

const MAX_WIDTH = 200;
const PADDING = 28;

type UseLpColumnResizeReturn = {
  widths: Record<string, number>;
  resizingId: string | null;
  makeHandleMouseDown: (lpId: string) => (event: React.MouseEvent) => void;
  makeHandleDoubleClick: (lpId: string) => () => void;
};

export function useLpColumnResize(
  lpIds: string[],
  initialWidth: number,
): UseLpColumnResizeReturn {
  const [widths, setWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(lpIds.map((id) => [id, initialWidth])),
  );
  const [resizingId, setResizingId] = useState<string | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  function makeHandleMouseDown(lpId: string) {
    return (event: React.MouseEvent) => {
      event.preventDefault();
      startXRef.current = event.clientX;
      startWidthRef.current = widths[lpId] ?? initialWidth;
      setResizingId(lpId);

      function handleMouseMove(moveEvent: MouseEvent) {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(LP_COLUMN_MIN_WIDTH, startWidthRef.current + delta),
        );
        setWidths((prev) => ({ ...prev, [lpId]: newWidth }));
      }

      function handleMouseUp() {
        setResizingId(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      }

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };
  }

  function makeHandleDoubleClick(lpId: string) {
    return () => {
      const header = document.querySelector<HTMLElement>(
        `[data-testid="lp-column-header-${lpId}"]`,
      );

      if (!header) return;

      const nameSpan = header.querySelector<HTMLElement>("span");

      if (!nameSpan) return;

      const savedMaxWidth = nameSpan.style.maxWidth;
      nameSpan.style.maxWidth = "none";
      const range = document.createRange();
      range.selectNodeContents(nameSpan);
      const naturalWidth =
        Math.ceil(range.getBoundingClientRect().width) + PADDING;
      nameSpan.style.maxWidth = savedMaxWidth;

      setWidths((prev) => ({
        ...prev,
        [lpId]: Math.min(
          MAX_WIDTH,
          Math.max(LP_COLUMN_MIN_WIDTH, naturalWidth),
        ),
      }));
    };
  }

  return { widths, resizingId, makeHandleMouseDown, makeHandleDoubleClick };
}
